// src/components/FloatingActionButtons/FloatingActionButtons.tsx
import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import styled from "styled-components";
import { FaWhatsapp, FaQuestionCircle, FaPaperPlane } from "react-icons/fa";

const ButtonsContainer = styled.div`
  position: fixed;
  bottom: clamp(70px, 10vh, 90px);
  right: clamp(15px, 3vw, 25px);
  display: flex;
  flex-direction: column;
  gap: clamp(8px, 2vh, 20px);
  z-index: 1000;

  /* Responsive breakpoints (same as before) */
  @media (min-width: 1920px) {
    bottom: 90px;
    right: 30px;
    gap: 10px;
  }
  @media (min-width: 1440px) and (max-width: 1919px) {
    bottom: 80px;
    right: 30px;
    gap: 10px;
  }
  @media (min-width: 1024px) and (max-width: 1439px) {
    bottom: 75px;
    right: 20px;
    gap: 10px;
  }
  @media (min-width: 768px) and (max-width: 1023px) {
    bottom: 65px;
    right: 20px;
    gap: 10px;
  }
  @media (min-width: 481px) and (max-width: 767px) {
    bottom: 55px;
    right: 15px;
    gap: 8px;
  }
  @media (max-width: 480px) {
    bottom: 55px;
    right: 15px;
    gap: 8px;
  }
  @media (max-width: 360px) {
    bottom: 45px;
    right: 15px;
    gap: 8px;
  }
`;

const ActionButton = styled.button<{ $bgColor: string }>`
  width: clamp(25px, 5vw, 50px);
  height: clamp(25px, 5vw, 50px);
  min-width: clamp(25px, 5vw, 50px);
  min-height: clamp(25px, 5vw, 50px);
  max-width: 50px;
  max-height: 50px;
  border-radius: 50%;
  background: ${(props) => props.$bgColor};
  color: white;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: clamp(16px, 3vw, 28px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  transition: all 0.3s ease;

  &:hover {
    transform: scale(1.1);
    box-shadow: 0 6px 16px rgba(0, 0, 0, 0.2);
  }
  &:active {
    transform: scale(0.95);
  }

  /* Responsive sizes (same as before) */
  @media (min-width: 1920px) {
    width: 50px;
    height: 50px;
    min-width: 50px;
    min-height: 50px;
    font-size: 26px;
  }
  @media (min-width: 1440px) and (max-width: 1919px) {
    width: 45px;
    height: 45px;
    min-width: 45px;
    min-height: 45px;
    font-size: 24px;
  }
  @media (min-width: 1024px) and (max-width: 1439px) {
    width: 40px;
    height: 40px;
    min-width: 40px;
    min-height: 40px;
    font-size: 21px;
  }
  @media (min-width: 768px) and (max-width: 1023px) {
    width: 36px;
    height: 36px;
    min-width: 36px;
    min-height: 36px;
    font-size: 19px;
  }
  @media (min-width: 481px) and (max-width: 767px) {
    width: 32px;
    height: 32px;
    min-width: 32px;
    min-height: 32px;
    font-size: 16px;
  }
  @media (min-width: 361px) and (max-width: 480px) {
    width: 28px;
    height: 28px;
    min-width: 28px;
    min-height: 28px;
    font-size: 14px;
  }
  @media (max-width: 360px) {
    width: 24px;
    height: 24px;
    min-width: 24px;
    min-height: 24px;
    font-size: 12px;
  }
`;

const Tooltip = styled.div`
  position: absolute;
  right: calc(100% + 10px);
  background: rgba(0, 0, 0, 0.85);
  color: white;
  padding: clamp(4px, 1vh, 8px) clamp(8px, 1.5vw, 16px);
  border-radius: 6px;
  font-size: clamp(10px, 1.2vw, 14px);
  white-space: nowrap;
  pointer-events: none;
  opacity: 0;
  transition: opacity 0.3s ease;
  backdrop-filter: blur(8px);
  font-weight: 500;

  ${ActionButton}:hover + & {
    opacity: 1;
  }

  @media (max-width: 768px) {
    display: none;
  }
  @media (min-width: 769px) and (max-width: 1024px) {
    font-size: 11px;
    right: calc(100% + 8px);
  }
`;

const FloatingActionButtons = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [showButtons, setShowButtons] = useState(true);

  useEffect(() => {
    const hiddenRoutes = ["/admin/signin", "/admin/dashboard"];
    setShowButtons(!hiddenRoutes.includes(location.pathname));
  }, [location]);

  if (!showButtons) return null;

  const showStudyFormButton = location.pathname === "/study-online";
  const showLearnFormButton = location.pathname === "/e-learning";
  const isFormPage = showStudyFormButton || showLearnFormButton;

  const handleWhatsAppClick = () => {
    const phoneNumber = "919247584861";
    const message = encodeURIComponent(
      "Hello Ekluvya Team,\n\n" +
        "I hope this message finds you well. I'm interested in learning more about your courses and services. Could you please share details about the features, offerings, and any other relevant information?\n\n" +
        "I look forward to your response. Thank you.",
    );
    const url = `https://wa.me/${phoneNumber}?text=${message}`;
    window.open(url, "_blank");
  };

  const handleFAQClick = () => {
    navigate("/faq");
  };

  const handleLearnFormClick = () => {
    const formElement = document.getElementById("e-learn");
    if (formElement) {
      const offset = 80;
      const y =
        formElement.getBoundingClientRect().top + window.pageYOffset - offset;
      window.scrollTo({ top: y, behavior: "smooth" });
    } else {
      navigate("/e-learning");
    }
  };

  const handleStudyFormClick = () => {
    const formElement = document.getElementById("study-online");
    if (formElement) {
      const offset = 80;
      const y =
        formElement.getBoundingClientRect().top + window.pageYOffset - offset;
      window.scrollTo({ top: y, behavior: "smooth" });
    } else {
      navigate("/study-online");
    }
  };

  return (
    <ButtonsContainer>
      {showStudyFormButton && (
        <div style={{ position: "relative" }}>
          <ActionButton
            $bgColor="#E8410A"
            onClick={handleStudyFormClick}
            aria-label="Google Form"
          >
            <FaPaperPlane />
          </ActionButton>
          <Tooltip>Google Form</Tooltip>
        </div>
      )}

      {showLearnFormButton && (
        <div style={{ position: "relative" }}>
          <ActionButton
            $bgColor="#E8410A"
            onClick={handleLearnFormClick}
            aria-label="Meta Form"
          >
            <FaPaperPlane />
          </ActionButton>
          <Tooltip>Meta Form</Tooltip>
        </div>
      )}

      {!isFormPage && (
        <>
          <div style={{ position: "relative" }}>
            <ActionButton
              $bgColor="#25D366"
              onClick={handleWhatsAppClick}
              aria-label="Contact us on WhatsApp"
            >
              <FaWhatsapp />
            </ActionButton>
            <Tooltip>WhatsApp</Tooltip>
          </div>

          <div style={{ position: "relative" }}>
            <ActionButton
              $bgColor="#0f3460"
              onClick={handleFAQClick}
              aria-label="FAQ"
            >
              <FaQuestionCircle />
            </ActionButton>
            <Tooltip>FAQ</Tooltip>
          </div>
        </>
      )}
    </ButtonsContainer>
  );
};

export default FloatingActionButtons;
